import { public_env, translate } from '@cfixe-monorepo/shared';
import type {
  SupplementQuantity,
  PublicBookingSource,
  PublicProNewsListResponseObject,
  PublicProNewsDetailObject,
  PublicPrestationsResponseObject,
  PublicPrestationDetailObject,
  PublicPrestationsHighlightResponseObject,
  PublicHighlightedPromotionsResponseObject,
  PublicTeamMemberObject,
  PublicPlaceObject,
  PublicProfessionalResponseObject,
  BookingPrestationsResponseObject,
  BookingBookabilityResponseObject,
  BookingEligibleMembersResponseObject,
  BookableAvailabilityResponseObject,
  BookableNextSlotResponseObject,
  ContactSubmissionRequestObject,
  CartSummaryRequestObject,
  CartSummaryResponseObject,
  PreReservationResponseObject,
  BookingConfirmationResponseObject,
  BookableSlotItemObject,
  CancelConfirmedBookingResponseObject,
  ProPaymentPublicStatusObject,
  AddressObject,
  AddressAutocompleteResponse,
  ValidateZoneAddressResponseObject,
  ProPaymentPublicDetailObject,
  RescheduleConfirmedBookingAvailabilityResponseObject,
  RescheduleConfirmedBookingResponseObject,
  PublicGoogleReviewsResponseObject,
  PublicPackItemObject,
  PublicPacksResponseObject,
  GiftCardPublicConfigResponseObject,
  GiftCardCheckoutRequestObject,
  GiftCardCheckoutResponseObject,
  GiftCardConfirmationResponseObject,
  GiftCardCartSummaryRequestObject,
  GiftCardCartSummaryResponseObject,
  AddressDetailsResponse,
  RidePublicConfigResponseObject,
  RidePriceCalculatorRequestObject,
  RidePriceCalculatorResponseObject,
  RideAvailabilityCheckRequestObject,
  RideAvailabilityCheckResponseObject,
  RideCheckoutRequestObject,
  RideCheckoutResponseObject,
  GoogleReserveConversionRequestObject,
} from '@cfixe-monorepo/shared';

const BACKEND_URL = public_env.API_ENDPOINT;

export class PublicApiError extends Error {
  readonly status: number;
  readonly errorKey: string | undefined;
  readonly body: unknown;
  /**
   * Phrase prête à afficher, construite par le backend pour CE refus (`data.user_message`,
   * ex. les horaires où un code promo est valable). Quand elle existe, `message` la porte
   * à la place du libellé générique de la clé.
   */
  readonly userMessage: string | undefined;

  constructor(status: number, message: string, errorKey: string | undefined, body: unknown, userMessage?: string) {
    super(message);
    this.name = 'PublicApiError';
    this.status = status;
    this.errorKey = errorKey;
    this.body = body;
    this.userMessage = userMessage;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isApiErrorKey(value: string): boolean {
  return /^[A-Z0-9_]+$/.test(value);
}

function extractApiErrorKeyFromValue(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return isApiErrorKey(value) ? value : undefined;
  }

  if (!isRecord(value)) return undefined;

  return (
    extractApiErrorKeyFromValue(value.json) ??
    extractApiErrorKeyFromValue(value.error) ??
    extractApiErrorKeyFromValue(value.key) ??
    extractApiErrorKeyFromValue(value.code)
  );
}

function extractApiErrorKey(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined;
  return extractApiErrorKeyFromValue(body.error) ?? extractApiErrorKeyFromValue(body.message);
}

function getFriendlyApiErrorMessage(status: number, errorKey: string | undefined): string {
  if (errorKey) {
    const translated = translate('fr-FR', 'API_ERRORS', errorKey);
    if (translated) return translated;
  }

  return translate('fr-FR', 'API_ERRORS', 'FRIENDLY_GLOBAL_ERROR') ?? `HTTP ${status}`;
}

async function readPublicApiErrorBody(res: Response): Promise<unknown> {
  try {
    const contentType = res.headers.get('content-type') ?? '';
    if (contentType.includes('application/json')) {
      return await res.json();
    }

    const text = await res.text();
    return text.trim() ? { message: text } : undefined;
  } catch {
    return undefined;
  }
}

/** `data.user_message` du corps d'erreur : phrase destinée au client final, sinon rien. */
function extractUserMessage(body: unknown): string | undefined {
  if (!isRecord(body) || !isRecord(body.data)) return undefined;
  const value = body.data.user_message;
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}

async function throwPublicApiError(res: Response): Promise<never> {
  const body = await readPublicApiErrorBody(res);
  const errorKey = extractApiErrorKey(body);
  const userMessage = extractUserMessage(body);
  throw new PublicApiError(
    res.status,
    userMessage ?? getFriendlyApiErrorMessage(res.status, errorKey),
    errorKey,
    body,
    userMessage,
  );
}

export type FetchOptions = {
  next?: { revalidate: number };
  signal?: AbortSignal;
  headers?: HeadersInit;
  /** Empreinte des promotions du site (`catalog_version`) : change l'URL, donc la clé du cache des données. */
  catalogVersion?: string;
};

/** Ajoute `catalog_version` à l'URL d'une prestation publique préchargée ; le backend l'ignore. */
function withCatalogVersion(url: string, options?: FetchOptions): string {
  if (!options?.catalogVersion) return url;
  return `${url}${url.includes('?') ? '&' : '?'}catalog_version=${encodeURIComponent(options.catalogVersion)}`;
}

function buildFetchInit(options?: FetchOptions): RequestInit {
  const init: RequestInit = {};
  if (options?.signal) init.signal = options.signal;
  if (options?.headers) init.headers = options.headers;
  // Next.js extends fetch() with a `next` option (ISR revalidate), but it's not in the standard RequestInit type
  if (options?.next) (init as Record<string, unknown>).next = options.next;
  return init;
}

// ─── News ────────────────────────────────────────────────────────────

export async function fetchNewsList(
  professionalSlug: string,
  params: {
    limit: number;
    offset: number;
    tag?: string;
    /** Id de tag (bloc « Actualités à la une » : le builder stocke l'id, pas le slug). */
    tagId?: string;
    search?: string;
    newsIds?: string[];
  },
  options?: FetchOptions,
): Promise<PublicProNewsListResponseObject> {
  const qs = new URLSearchParams();
  qs.set('limit', String(params.limit));
  qs.set('offset', String(params.offset));
  if (params.tag) qs.set('tag', params.tag);
  if (params.tagId) qs.set('tag_id', params.tagId);
  if (params.search) qs.set('search', params.search);
  // Sélection manuelle d'articles (footer, actualités à la une) : le backend restreint
  // la liste à ces ids et respecte l'ordre demandé.
  if (params.newsIds?.length) qs.set('news_ids', params.newsIds.join(','));

  const res = await fetch(
    `${BACKEND_URL}/public/professional/${professionalSlug}/news?${qs.toString()}`,
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicProNewsListResponseObject;
}

export async function fetchNewsDetail(
  professionalSlug: string,
  newsSlug: string,
  options?: FetchOptions,
): Promise<PublicProNewsDetailObject> {
  const res = await fetch(
    `${BACKEND_URL}/public/professional/${professionalSlug}/news/${newsSlug}`,
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicProNewsDetailObject;
}

// ─── Prestations ─────────────────────────────────────────────────────

export async function fetchPrestationsList(
  professionalSlug: string,
  params: { limit: number; offset: number; categorySlug?: string; prestationIds?: string[] },
  options?: FetchOptions,
): Promise<PublicPrestationsResponseObject> {
  const qs = new URLSearchParams({ limit: String(params.limit), offset: String(params.offset) });
  // Scope la liste à une catégorie (page /prestations/<slug>) pour ne pas dépendre de la
  // pagination globale : la réponse renvoie toujours la liste complète des catégories.
  if (params.categorySlug) qs.set('category_slug', params.categorySlug);
  // Sélection manuelle de prestations (footer) : le backend restreint la liste à ces ids
  // et respecte l'ordre demandé.
  if (params.prestationIds?.length) qs.set('prestation_ids', params.prestationIds.join(','));
  const res = await fetch(
    withCatalogVersion(`${BACKEND_URL}/public/professional/${professionalSlug}/prestations?${qs.toString()}`, options),
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicPrestationsResponseObject;
}

export async function fetchPrestationDetail(
  professionalSlug: string,
  prestationSlug: string,
  options?: FetchOptions,
): Promise<PublicPrestationDetailObject> {
  const res = await fetch(
    withCatalogVersion(`${BACKEND_URL}/public/professional/${professionalSlug}/prestations/${prestationSlug}`, options),
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicPrestationDetailObject;
}

// ─── Google Reviews (sourced from the Google My Business sync) ───────
// The backend endpoint is not built yet; callers (see fetchInitialGoogleReviews)
// must degrade gracefully so the absent endpoint never breaks SSR or the page.

export async function fetchGoogleReviews(
  professionalSlug: string,
  params: { sourceIds?: string[]; minRating?: number; maxItems?: number },
  options?: FetchOptions,
): Promise<PublicGoogleReviewsResponseObject> {
  const qs = new URLSearchParams();
  if (params.minRating !== undefined) qs.set('min_rating', String(params.minRating));
  if (params.maxItems !== undefined) qs.set('limit', String(params.maxItems));
  for (const sourceId of params.sourceIds ?? []) qs.append('source_id', sourceId);

  const res = await fetch(
    `${BACKEND_URL}/public/professional/${professionalSlug}/google-reviews?${qs.toString()}`,
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicGoogleReviewsResponseObject;
}

export async function fetchPrestationsHighlight(
  professionalSlug: string,
  prestationIds?: string[],
  options?: FetchOptions,
): Promise<PublicPrestationsHighlightResponseObject> {
  const qs = prestationIds?.length ? `?prestation_ids=${prestationIds.join(',')}` : '';
  const res = await fetch(
    withCatalogVersion(`${BACKEND_URL}/public/professional/${professionalSlug}/prestations/highlight${qs}`, options),
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicPrestationsHighlightResponseObject;
}

/** Promotions mises en avant et ouvertes : « Promotion liée » du bandeau haut et de la section Promotion. */
export async function fetchHighlightedPromotions(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicHighlightedPromotionsResponseObject> {
  const res = await fetch(
    withCatalogVersion(`${BACKEND_URL}/public/professional/${professionalSlug}/promotions/highlighted`, options),
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicHighlightedPromotionsResponseObject;
}

// ─── Team ────────────────────────────────────────────────────────────

export async function fetchTeamList(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicTeamMemberObject[]> {
  const res = await fetch(
    `${BACKEND_URL}/public/professional/${professionalSlug}/team?limit=50&offset=0`,
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicTeamMemberObject[];
}

export async function fetchPackListing(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicPackItemObject[]> {
  const res = await fetch(`${BACKEND_URL}/public/professional/${professionalSlug}/packs`, buildFetchInit(options));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = (await res.json()) as PublicPacksResponseObject;
  return data.packs ?? [];
}

// ─── Professional (social links, info) ───────────────────────────────

/** In-flight dedup: reuse the same promise when multiple components request the same slug concurrently. */
const professionalInflight = new Map<string, Promise<PublicProfessionalResponseObject>>();

export function fetchProfessionalPublic(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicProfessionalResponseObject> {
  // Dedup only when there is no AbortSignal (signal-bound requests are caller-owned)
  if (options?.signal) {
    return fetchProfessionalPublicRaw(professionalSlug, options);
  }

  const existing = professionalInflight.get(professionalSlug);
  if (existing) return existing;

  const promise = fetchProfessionalPublicRaw(professionalSlug, options).finally((): void => {
    professionalInflight.delete(professionalSlug);
  });
  professionalInflight.set(professionalSlug, promise);
  return promise;
}

async function fetchProfessionalPublicRaw(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicProfessionalResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/professional/${professionalSlug}`, buildFetchInit(options));
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicProfessionalResponseObject;
}

// ─── Booking ────────────────────────────────────────────────────────

export async function fetchBookingPrestations(
  professionalSlug: string,
  params?: {
    prestation_ids?: number[];
    category_ids?: number[];
    place_ids?: number[];
    member_ids?: number[];
  },
  options?: FetchOptions,
): Promise<BookingPrestationsResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/prestations`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params ?? {}),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookingPrestationsResponseObject;
}

/**
 * Endpoint léger de recalcul `is_bookable` (conflit temporel multi-prestation). Renvoie une liste
 * compacte de booléens — voir `BookingPublicController.computeBookability`. Pensé pour être annulable
 * via `options.signal` (AbortController) lors d'appels live debouncés.
 */
export async function checkBookingBookability(
  professionalSlug: string,
  params: {
    slots: {
      prestation_id: number;
      duration?: number;
      team_member_id?: number;
      quantity?: number;
      selected_supplement_ids?: number[];
    }[];
    candidate_prestation_ids?: number[];
    pro_place_id?: number;
    tolerance_minutes?: number;
  },
  options?: FetchOptions,
): Promise<BookingBookabilityResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/bookability`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookingBookabilityResponseObject;
}

/**
 * Éligibilité membre par position de chaîne (étape « choisir par membre », multi-prestation).
 * Annulable via `options.signal` (appels live debouncés). Voir `BookingPublicController.getChainEligibleMembers`.
 */
export async function checkBookingEligibleMembers(
  professionalSlug: string,
  params: {
    slots: {
      prestation_id: number;
      pro_place_id: number;
      duration: number;
      quantity?: number;
      team_member_id?: number;
    }[];
    tolerance_minutes?: number;
  },
  options?: FetchOptions,
): Promise<BookingEligibleMembersResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/eligible-members`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookingEligibleMembersResponseObject;
}

export async function fetchBookingSlots(
  professionalSlug: string,
  params: {
    start_date?: string;
    slots: {
      prestation_id: number;
      duration?: number;
      team_member_id?: number;
      team_member_ids?: number[];
      pro_place_id: number;
      quantity?: number;
      selected_price_index?: number;
      selected_supplement_ids?: number[];
      pro_resource_ids?: number[];
      party_size?: number;
    }[];
    /* Séances de groupe : inclure les créneaux sous-capacité (remaining_places), grisés côté UI. */
    include_session_slots_below_party_size?: boolean;
  },
  options?: FetchOptions,
): Promise<BookableAvailabilityResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/bookable`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookableAvailabilityResponseObject;
}

/** Prochain créneau d'une semaine vide (P3) : même charge utile que `fetchBookingSlots`. */
export async function fetchNextBookableSlot(
  professionalSlug: string,
  params: Parameters<typeof fetchBookingSlots>[1],
  options?: FetchOptions,
): Promise<BookableNextSlotResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/bookable/next`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookableNextSlotResponseObject;
}

/**
 * Demande envoyée par le circuit du formulaire de contact du site (`contact-submission`, captcha) : la demande
 * de devis du tunnel y passe aussi.
 */
export async function submitContactRequest(
  professionalSlug: string,
  body: ContactSubmissionRequestObject,
  capToken: string,
): Promise<void> {
  const res = await fetch(`${BACKEND_URL}/public/professional/${professionalSlug}/contact-submission`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(body),
  });
  if (!res.ok) await throwPublicApiError(res);
}

export async function fetchCartSummary(
  professionalSlug: string,
  params: CartSummaryRequestObject,
  options?: FetchOptions,
): Promise<CartSummaryResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/cart-summary`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as CartSummaryResponseObject;
}

export async function createPreReservation(
  professionalSlug: string,
  params: {
    slots: {
      slot_start: string;
      slot_end: string;
      team_member_id?: number;
      team_member_ids?: number[];
      pro_place_id: number;
      prestation_id: number;
      quantity: number;
      selected_price_index?: number;
      selected_supplement_ids?: number[];
      /** Quantités choisies des suppléments retenus (absent : une par unité facturée). */
      supplement_quantities?: SupplementQuantity[];
      selected_resource_id?: number;
      party_size?: number;
    }[];
    customer: {
      firstname: string;
      lastname: string;
      email: string;
      phone?: string;
    };
    note?: string;
    voucher_code?: string;
    promo_code?: string;
    customer_address?: AddressObject;
    custom_field_answers?: { booking_custom_field_id: number; value: string }[];
    /** Canal d'entrée (statistiques de provenance) — cf. createBookingCallbacks `source`. */
    source?: PublicBookingSource;
    /** Campagne à l'origine de la visite (`?cid=`), validée côté backend. */
    campaign_id?: number;
  },
  options?: FetchOptions,
): Promise<PreReservationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/pre-reservation`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as PreReservationResponseObject;
}

export async function applyVoucherToPreReservation(
  uuid: string,
  voucherCode?: string,
  options?: FetchOptions,
): Promise<PreReservationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/voucher`, {
    ...buildFetchInit(options),
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ voucher_code: voucherCode }),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as PreReservationResponseObject;
}

/** Champ unique « Code promo ou carte cadeau » : le serveur résout la nature du code et garde l'autre code posé. */
export async function applyCodeToPreReservation(
  uuid: string,
  code: string,
  options?: FetchOptions,
): Promise<PreReservationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/code`, {
    ...buildFetchInit(options),
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as PreReservationResponseObject;
}

export async function applyPromoToPreReservation(
  uuid: string,
  promoCode?: string,
  options?: FetchOptions,
): Promise<PreReservationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/promo`, {
    ...buildFetchInit(options),
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ promo_code: promoCode }),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as PreReservationResponseObject;
}

export async function confirmPreReservation(
  uuid: string,
  options?: FetchOptions,
): Promise<ProPaymentPublicStatusObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/confirm`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as ProPaymentPublicStatusObject;
}

export async function confirmPayOnSitePreReservation(
  uuid: string,
  options?: FetchOptions,
): Promise<ProPaymentPublicStatusObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/confirm-pay-on-site`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as ProPaymentPublicStatusObject;
}

/** Fichier .ics d'une réservation (« Ajouter au calendrier ») : lien direct, ouvert par le navigateur. */
export function bookingCalendarIcsUrl(uuid: string): string {
  return `${BACKEND_URL}/public/booking/${encodeURIComponent(uuid)}/calendar.ics`;
}

export async function cancelPreReservation(uuid: string, options?: FetchOptions): Promise<void> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}`, {
    ...buildFetchInit(options),
    method: 'DELETE',
    // « Retour au site » libère le créneau pendant que la page part : la requête doit survivre à la navigation.
    keepalive: true,
  });
  if (!res.ok) await throwPublicApiError(res);
}

export async function getBookingConfirmation(
  uuid: string,
  options?: FetchOptions,
): Promise<BookingConfirmationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/confirmation`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as BookingConfirmationResponseObject;
}

export async function cancelConfirmedBooking(
  uuid: string,
  params: { cancellation_reason?: string },
  capToken: string,
  options?: FetchOptions,
): Promise<CancelConfirmedBookingResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/cancel`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as CancelConfirmedBookingResponseObject;
}

export async function fetchRescheduleBookingSlots(
  uuid: string,
  params: { start_date?: string },
  capToken: string,
  options?: FetchOptions,
): Promise<RescheduleConfirmedBookingAvailabilityResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/reschedule/bookable`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RescheduleConfirmedBookingAvailabilityResponseObject;
}

export async function rescheduleConfirmedBooking(
  uuid: string,
  params: { slots: BookableSlotItemObject[]; reschedule_reason?: string },
  capToken: string,
  options?: FetchOptions,
): Promise<RescheduleConfirmedBookingResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/reschedule`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RescheduleConfirmedBookingResponseObject;
}

/**
 * Load Stripe payment details for a paid pre-reservation.
 * The backend lazily creates the Stripe PaymentIntent and returns its client_secret.
 */
export async function fetchProPaymentDetails(
  uuid: string,
  options?: FetchOptions,
): Promise<ProPaymentPublicDetailObject> {
  const res = await fetch(`${BACKEND_URL}/public/pro-payment/${uuid}`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as ProPaymentPublicDetailObject;
}

/** Poll the payment/reservation status of a booking after a Stripe confirmation. */
export async function fetchBookingStatus(uuid: string, options?: FetchOptions): Promise<ProPaymentPublicStatusObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${uuid}/status`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as ProPaymentPublicStatusObject;
}

// ─── Contact (places) ────────────────────────────────────────────────

export async function fetchContactPlaces(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<PublicPlaceObject[]> {
  const res = await fetch(
    `${BACKEND_URL}/public/professional/${professionalSlug}/places?limit=20&offset=0`,
    buildFetchInit(options),
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as PublicPlaceObject[];
}

// ─── Ride (tunnel VTC) ───────────────────────────────────────────────

export async function fetchRideConfig(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<RidePublicConfigResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/ride/${professionalSlug}/config`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RidePublicConfigResponseObject;
}

/** Estimation de course (recalculée serveur ; captcha — déclenche des appels Google Routes payants). */
export async function computeRidePrice(
  params: RidePriceCalculatorRequestObject,
  capToken: string,
  options?: FetchOptions,
): Promise<RidePriceCalculatorResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/ride/price-calculator`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RidePriceCalculatorResponseObject;
}

/**
 * Pré-check CONSULTATIF de disponibilité (à la saisie de l'heure). Sans captcha : lecture
 * seule DB côté backend, et un captcha ici brûlerait le token Cap à usage unique du devis.
 */
export async function checkRideAvailability(
  professionalSlug: string,
  params: RideAvailabilityCheckRequestObject,
  options?: FetchOptions,
): Promise<RideAvailabilityCheckResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/ride/${professionalSlug}/availability`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RideAvailabilityCheckResponseObject;
}

export async function createRideCheckout(
  professionalSlug: string,
  params: RideCheckoutRequestObject,
  capToken: string,
  options?: FetchOptions,
): Promise<RideCheckoutResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/ride/${professionalSlug}/checkout`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as RideCheckoutResponseObject;
}

/** Résout un place_id Google en AddressObject complète (lat/lng) — pendant du fetchAddressAutocomplete. */
export async function fetchAddressDetails(
  placeId: string,
  capToken: string,
  options?: FetchOptions,
): Promise<AddressDetailsResponse> {
  const res = await fetch(`${BACKEND_URL}/public/address/details?place_id=${encodeURIComponent(placeId)}`, {
    ...buildFetchInit(options),
    headers: { 'x-cap-token': capToken },
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as AddressDetailsResponse;
}

// ─── Address (autocomplete + zone validation) ────────────────────────

/**
 * `professionalSlug` (optionnel) : slug ou public_widget_id du pro du tunnel — le backend biaise
 * (soft) les suggestions autour de lui (point de départ VTC, sinon son adresse). Sans slug,
 * l'URL reste strictement identique (suggestions non biaisées).
 */
export async function fetchAddressAutocomplete(
  query: string,
  capToken: string,
  professionalSlug?: string,
  options?: FetchOptions,
): Promise<AddressAutocompleteResponse> {
  const proParam = professionalSlug ? `&professional_slug=${encodeURIComponent(professionalSlug)}` : '';
  const res = await fetch(`${BACKEND_URL}/public/address/autocomplete?query=${encodeURIComponent(query)}${proParam}`, {
    ...buildFetchInit(options),
    headers: { 'x-cap-token': capToken },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as AddressAutocompleteResponse;
}

export async function validateZoneAddress(
  professionalSlug: string,
  params: { pro_place_id: number; google_place_id: string },
  capToken: string,
  options?: FetchOptions,
): Promise<ValidateZoneAddressResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/booking/${professionalSlug}/validate-zone-address`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as ValidateZoneAddressResponseObject;
}

/**
 * Révèle des bindings protégés (`pro:phone`, `pro:email`) via l'endpoint captcha-protégé —
 * le MÊME chemin que ContactReveal sur les sites publics. Ces valeurs ne sont jamais servies
 * en clair dans une réponse publique : le masquage est notre protection anti-scraping, et
 * l'ajouter à un profil public la contournerait pour tous les pros concernés.
 */
export async function revealProtectedBindings(
  professionalSlug: string,
  keys: string[],
  capToken: string,
  options?: FetchOptions,
): Promise<Record<string, string>> {
  const res = await fetch(`${BACKEND_URL}/public/professional/${professionalSlug}/reveal-bindings`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify({ keys }),
  });
  if (!res.ok) await throwPublicApiError(res);
  const data = (await res.json()) as { values?: Record<string, string> };
  return data.values ?? {};
}

/**
 * Conversion Reserve with Google (intégration Redirect) : relaie le `rwg_token`
 * capturé sur `/book/{slug}` au backend, qui le poste à Google avec le partner id.
 * Fire-and-forget : `keepalive` pour survivre à une fermeture d'onglet, erreurs à
 * avaler côté appelant (le tracking ne doit jamais impacter le parcours).
 */
export async function reportGoogleReserveConversion(params: GoogleReserveConversionRequestObject): Promise<void> {
  await fetch(`${BACKEND_URL}/public/google-reserve/conversion`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
    keepalive: true,
  });
}

// ─── Gift card / pack purchase ───────────────────────────────────────

export async function fetchGiftCardConfig(
  professionalSlug: string,
  options?: FetchOptions,
): Promise<GiftCardPublicConfigResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/gift-card/${professionalSlug}/config`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as GiftCardPublicConfigResponseObject;
}

/** Aperçu panier (mini-panier + total formaté serveur). Public, sans captcha (appelé à chaque changement de sélection). */
export async function fetchGiftCardCartSummary(
  professionalSlug: string,
  params: GiftCardCartSummaryRequestObject,
  options?: FetchOptions,
): Promise<GiftCardCartSummaryResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/gift-card/${professionalSlug}/cart-summary`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as GiftCardCartSummaryResponseObject;
}

export async function createGiftCardCheckout(
  professionalSlug: string,
  params: GiftCardCheckoutRequestObject,
  capToken: string,
  options?: FetchOptions,
): Promise<GiftCardCheckoutResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/gift-card/${professionalSlug}/checkout`, {
    ...buildFetchInit(options),
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-cap-token': capToken },
    body: JSON.stringify(params),
  });
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as GiftCardCheckoutResponseObject;
}

export async function fetchGiftCardConfirmation(
  paymentUuid: string,
  options?: FetchOptions,
): Promise<GiftCardConfirmationResponseObject> {
  const res = await fetch(`${BACKEND_URL}/public/gift-card/confirmation/${paymentUuid}`, buildFetchInit(options));
  if (!res.ok) await throwPublicApiError(res);
  return (await res.json()) as GiftCardConfirmationResponseObject;
}
